import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Home } from './components/Home';
import { EmployerLogin } from './components/employer/EmployerLogin';
import { EmployerDashboard } from './components/employer/EmployerDashboard';
import { EmployerSettings } from './components/employer/EmployerSettings';
import { CandidateProfile } from './components/employer/CandidateProfile';
import { CandidateLogin } from './components/candidate/CandidateLogin';
import { CandidateDashboard } from './components/candidate/CandidateDashboard';
import { CandidateSettings } from './components/candidate/CandidateSettings';
import { ProfileStrength } from './components/candidate/ProfileStrength';
import { Applications } from './components/candidate/Applications';
import { SavedJobs } from './components/candidate/SavedJobs';
import { JobMatches } from './components/candidate/JobMatches';
import { ProfileSectionEditor } from './pages/candidate/ProfileSectionEditor';
import { SubscriptionPortal } from './components/subscription/SubscriptionPortal';
import { AdminPortal } from './components/admin/AdminPortalEnhanced';
import { AdminLogin } from './components/admin/AdminLogin';
import { AdminSettings } from './components/admin/AdminSettings';
import { HelpCenter } from './components/info/HelpCenter';
import { LifePlatform } from './components/life/LifePlatform';
import { ReturnerHub } from './components/ReturnerHub';
import { JobsIndex } from './pages/jobs/JobsIndex';
import { JobDetail } from './pages/jobs/JobDetail';
import { CompanyProfile } from './pages/company/CompanyProfile';
import { PricingPage } from './pages/shared/PricingPage';
import { PrivacyPolicyPage } from './pages/shared/PrivacyPolicyPage';
import { TermsPage } from './pages/shared/TermsPage';
import { CookiesPage } from './pages/shared/CookiesPage';
import { MaintenancePage } from './pages/shared/MaintenancePage';
import { ContactPage } from './pages/shared/ContactPage';
import { CandidateOnboarding } from './pages/candidate/CandidateOnboarding';
import { CandidateCvPage } from './pages/candidate/CandidateCvPage';
import { PracticeHubPage } from './pages/candidate/PracticeHubPage';
import { CareerPathPage } from './pages/candidate/CareerPathPage';
import { CommuteCalculatorPage } from './pages/candidate/CommuteCalculatorPage';
import { JobManagement } from './pages/employer/JobManagement';
import { JobCreate } from './pages/employer/JobCreate';
import { JobEditor } from './pages/employer/JobEditor';
import { JobWizard } from './pages/employer/JobWizard';
import { TalentSearchPage } from './pages/employer/TalentSearch';
import { SkillTestCreatePage } from './pages/employer/SkillTestCreatePage';
import { SkillTestEditPage } from './pages/employer/SkillTestEditPage';
import { EmployerAnalytics } from './pages/employer/EmployerAnalytics';
import { ModerationQueue } from './pages/admin/ModerationQueue';
import { LeadManagement } from './pages/admin/LeadManagement';
import { AdminAnalytics } from './pages/admin/AdminAnalytics';
import { CandidateOversight } from './pages/admin/CandidateOversight';
import { EmployerVerification } from './pages/admin/EmployerVerification';
import { AuditViewer } from './pages/admin/AuditViewer';
import { AdminUploadsDlq } from './pages/admin/AdminUploadsDlq';
import { WwwJobsAdmin } from './pages/admin/WwwJobsAdmin';
import { WwwBlogAdmin } from './pages/admin/WwwBlogAdmin';
import { NotificationCenter } from './pages/shared/NotificationCenter';
import { SettingsPage } from './pages/shared/SettingsPage';
import { NotFoundPage } from './pages/shared/NotFoundPage';
import { Toaster } from './components/ui/sonner';
import { SiteNavigation } from './components/layout/SiteNavigation';
import { CommandPalette } from './components/layout/CommandPalette';
import { ScrollRestoration } from './components/layout/ScrollRestoration';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { MobileActionBar } from './components/layout/MobileActionBar';
import { SkillTestsProvider } from './hooks/useSkillTests';
import { queryClient } from './lib/queryClient';
import { useAuth } from './hooks/useAuth';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { env } from './lib/env';

export function DevLogoutButton() {
  const { logout } = useAuth();
  return (
    <button
      type="button"
      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      className="fixed bottom-4 right-4 rounded bg-red-600 px-4 py-2 text-white"
    >
      Force Logout
    </button>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <SkillTestsProvider>
          <ScrollRestoration />
          <div className="relative min-h-screen overflow-hidden text-white">
            <div className="pointer-events-none fixed inset-0 -z-10 bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]" />
            <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-[#ba9036] focus:px-4 focus:py-2 focus:text-black">
              Skip to main content
            </a>
            <SiteNavigation />
            <AuthErrorListener />
            <CommandPalette />
            <main id="main" className="pt-16">
              <Routes>
                <Route path="/" element={<Home />} />
              <Route path="/jobs" element={<JobsIndex />} />
              <Route path="/jobs/:jobSlug" element={<JobDetail />} />
              <Route path="/company/:companySlug" element={<CompanyProfile />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/maintenance" element={<MaintenancePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/employer" element={<EmployerLogin />} />
              <Route path="/employer/dashboard" element={<ProtectedRoute requiredRole="employer"><EmployerDashboard /></ProtectedRoute>} />
              <Route path="/employer/settings" element={<ProtectedRoute requiredRole="employer"><EmployerSettings /></ProtectedRoute>} />
              <Route path="/employer/jobs" element={<ProtectedRoute requiredRole="employer"><JobManagement /></ProtectedRoute>} />
              <Route path="/employer/job/new" element={<ProtectedRoute requiredRole="employer"><JobCreate /></ProtectedRoute>} />
              <Route path="/employer/job/wizard" element={<ProtectedRoute requiredRole="employer"><JobWizard /></ProtectedRoute>} />
              <Route path="/employer/job/:jobId/edit" element={<ProtectedRoute requiredRole="employer"><JobEditor /></ProtectedRoute>} />
              <Route path="/employer/jobs/:jobId/edit" element={<ProtectedRoute requiredRole="employer"><JobEditor /></ProtectedRoute>} />
              <Route path="/employer/search" element={<ProtectedRoute requiredRole="employer"><TalentSearchPage /></ProtectedRoute>} />
              <Route path="/employer/tests/new" element={<ProtectedRoute requiredRole="employer"><SkillTestCreatePage /></ProtectedRoute>} />
              <Route path="/employer/tests/:testId/edit" element={<ProtectedRoute requiredRole="employer"><SkillTestEditPage /></ProtectedRoute>} />
              <Route path="/employer/analytics" element={<ProtectedRoute requiredRole="employer"><EmployerAnalytics /></ProtectedRoute>} />
              <Route path="/employer/candidate/:id" element={<ProtectedRoute requiredRole="employer"><CandidateProfile /></ProtectedRoute>} />
              <Route path="/candidate" element={<CandidateLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/candidate/onboarding" element={<ProtectedRoute><CandidateOnboarding /></ProtectedRoute>} />
              <Route path="/candidate/dashboard" element={<ProtectedRoute><CandidateDashboard /></ProtectedRoute>} />
              <Route path="/candidate/team-dashboard" element={<ProtectedRoute requireTeamCandidate><CandidateDashboard teamMode /></ProtectedRoute>} />
              <Route path="/candidate/cv" element={<ProtectedRoute><CandidateCvPage /></ProtectedRoute>} />
              <Route path="/candidate/profile" element={<ProtectedRoute><ProfileSectionEditor /></ProtectedRoute>} />
              <Route path="/candidate/practice" element={<ProtectedRoute><PracticeHubPage /></ProtectedRoute>} />
              <Route path="/candidate/path" element={<ProtectedRoute><CareerPathPage /></ProtectedRoute>} />
              <Route path="/candidate/commute" element={<ProtectedRoute><CommuteCalculatorPage /></ProtectedRoute>} />
              <Route path="/candidate/settings" element={<ProtectedRoute><CandidateSettings /></ProtectedRoute>} />
              <Route path="/candidate/profile-strength" element={<ProtectedRoute><ProfileStrength /></ProtectedRoute>} />
              <Route path="/candidate/profile-section/:sectionId" element={<ProtectedRoute><ProfileSectionEditor /></ProtectedRoute>} />
              <Route path="/candidate/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />
              <Route path="/candidate/saved" element={<ProtectedRoute><SavedJobs /></ProtectedRoute>} />
              <Route path="/candidate/saved-jobs" element={<ProtectedRoute><SavedJobs /></ProtectedRoute>} />
              <Route path="/candidate/job-matches" element={<ProtectedRoute><JobMatches /></ProtectedRoute>} />
              <Route path="/subscription" element={<SubscriptionPortal />} />
              <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminPortal /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute requiredRole="admin"><AdminSettings /></ProtectedRoute>} />
              <Route path="/admin/moderation" element={<ProtectedRoute requiredRole="admin"><ModerationQueue /></ProtectedRoute>} />
              <Route path="/admin/leads" element={<ProtectedRoute requiredRole="admin"><LeadManagement /></ProtectedRoute>} />
              <Route path="/admin/candidates" element={<ProtectedRoute requiredRole="admin"><CandidateOversight /></ProtectedRoute>} />
              <Route path="/admin/employers" element={<ProtectedRoute requiredRole="admin"><EmployerVerification /></ProtectedRoute>} />
              <Route path="/admin/uploads" element={<ProtectedRoute requiredRole="admin"><AdminUploadsDlq /></ProtectedRoute>} />
              <Route path="/admin/analytics" element={<ProtectedRoute requiredRole="admin"><AdminAnalytics /></ProtectedRoute>} />
              <Route path="/admin/www/jobs" element={<ProtectedRoute requiredRole="admin"><WwwJobsAdmin /></ProtectedRoute>} />
              <Route path="/admin/www/blog" element={<ProtectedRoute requiredRole="admin"><WwwBlogAdmin /></ProtectedRoute>} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/life" element={<LifePlatform />} />
              <Route path="/returner-hub" element={<ReturnerHub />} />
              <Route path="/notifications" element={<NotificationCenter />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/admin/audit/:entity/:entityId" element={<ProtectedRoute requiredRole="admin"><AuditViewer /></ProtectedRoute>} />
              <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            <MobileActionBar />
            <Toaster richColors position="top-right" />
            <TempLogoutButton />
          </div>
        </SkillTestsProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;

function TempLogoutButton() {
  const { logout } = useAuth();
  return (
    <button
      type="button"
      onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
      className="fixed bottom-6 right-6 z-50 rounded-full bg-red-600/80 px-4 py-2 text-sm font-medium text-white shadow-lg shadow-red-900/30 backdrop-blur hover:bg-red-600"
    >
      Force Logout
    </button>
  );
}

function AuthErrorListener() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    const description = params.get('error_description');

    if (!error) return;

    const message = description ? decodeURIComponent(description) : 'Authentication error occurred.';
    toast.error(message);

    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    window.history.replaceState({}, document.title, url.toString());
  }, [location.search]);

  return null;
}
